import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
  useRef,
} from "react";
import FingerprintJS from "@fingerprintjs/fingerprintjs";

// Константа для ключа хранения выбранной роли
const ONBOARDING_ROLE_KEY = "onboardingRole";

// 🆕 Workers Auth: типи для сумісності
interface Session {
  access_token: string;
  refresh_token: string;
  user: SupabaseUser;
}

interface SupabaseUser {
  id: string;
  email?: string;
  telegram_id?: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  [key: string]: unknown;
}

declare global {
  interface Window {
    __AUTH_PROFILE_TIMEOUT_MS__?: number;
    /** Diagnostics: counts how many times AuthProvider mounted */
    __AUTH_PROVIDER_MOUNT_COUNT__?: number;
    /** Diagnostics: last unmount timestamp */
    __AUTH_PROVIDER_LAST_UNMOUNT__?: number;
    /** Cached FingerprintJS agent instance */
    __FINGERPRINT_AGENT__?: unknown;
  }
}
import {
  userApi,
  User as AppUser,
} from "@/api/database";
import * as workersAuth from "@/lib/workers-auth";
import type { WorkersAuthSession } from "@/lib/workers-auth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useSecurityMonitoring } from "@/lib/security";
interface EdgeFunctionResponse {
  access_token?: string;
  refresh_token?: string;
  isValid?: boolean;
  user?: { id: number; username?: string; first_name: string };
  is_new_user?: boolean;
  user_role?: string | null;
}

type AuthStatus =
  | "initializing"
  | "unauthenticated"
  | "authenticating"
  | "authenticated"
  | "loading_profile"  // 🆕 Session exists, but profile is loading
  | "ready"            // 🆕 Session + profile fully loaded
  | "error";

interface AuthContextType {
  session: Session | null;
  user: SupabaseUser | null;
  userProfile: AppUser | null;
  /** (Deprecated alias) Use appLoading instead. */
  isLoading: boolean;
  /** Consolidated loading flag for UI components. */
  appLoading: boolean;
  /** True when a valid Supabase session exists (profile may still be pending). */
  isAuthenticated: boolean;
  /** True when auth listener finished initial check (session known). */
  isAuthReady: boolean;
  isAuthenticating: boolean;
  authError: string | null;
  isAuthenticationAttempted: boolean;
  /** Explicit finite-state status for auth lifecycle */
  authStatus: AuthStatus;
  /** Determined during authentication edge function call */
  isNewUser: boolean | null;
  /** Hard timeout reached for profile loading */
  profileLoadExpired: boolean;
  signOut: () => void;
  authenticateWithTelegram: (
    initData: string,
    turnstileToken?: string,
  ) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  userProfile: null,
  isLoading: true,
  appLoading: true,
  isAuthenticated: false,
  isAuthReady: false,
  isAuthenticating: false,
  authError: null,
  isAuthenticationAttempted: false,
  authStatus: "initializing",
  isNewUser: null,
  profileLoadExpired: false,
  signOut: () => {},
  authenticateWithTelegram: async () => false,
});

const RealAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Real auth provider (non-design mode)
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [isLoading, setIsLoading] = useState(true); // deprecated, keep for backward compatibility
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false); // завершена ли инициализация listener
  const [authError, setAuthError] = useState<string | null>(null);
  const [isAuthenticationAttempted, setIsAuthenticationAttempted] =
    useState(false);
  const [authStatus, setAuthStatus] = useState<AuthStatus>("initializing");
  const [isNewUser, setIsNewUser] = useState<boolean | null>(null);
  const [profileLoadExpired, setProfileLoadExpired] = useState(false);
  const profileStartRef = useRef<number | null>(null);
  const authTransitionRef = useRef<{ from: AuthStatus; at: number } | null>(
    null,
  );
  const { identifyUser, trackEvent, posthog } = useAnalytics();
  const securityMonitor = useSecurityMonitoring();
  const queryClient = useQueryClient();

  // continue with real provider

  /**
   * Single authoritative method for FSM status transitions.
   * Ensures we don't have mixed direct setAuthStatus + external logging causing duplicate logs.
   */
  const trackAuthTransition = useCallback(
    (to: AuthStatus) => {
      setAuthStatus((prev) => {
        if (prev === to) return prev;
        const now = performance.now();
        const from = prev;
        trackEvent?.("auth_transition", {
          from,
          to,
          elapsed_ms_since_prev: authTransitionRef.current
            ? now - authTransitionRef.current.at
            : 0,
        });
        authTransitionRef.current = { from: to, at: now };
        console.log("[AuthFSM] Transition", { from, to });
        return to;
      });
    },
    [trackEvent],
  );

  // Auth state listener: must mount exactly once. Re-mounting causes churn and duplicate transitions.
  useEffect(() => {
    window.__AUTH_PROVIDER_MOUNT_COUNT__ =
      (window.__AUTH_PROVIDER_MOUNT_COUNT__ || 0) + 1;
    const mountCount = window.__AUTH_PROVIDER_MOUNT_COUNT__;
    console.log("[AuthContext] Setting up Workers auth state listener (one-shot)...", {
      mountCount,
      sinceLastUnmountMs: window.__AUTH_PROVIDER_LAST_UNMOUNT__
        ? Date.now() - window.__AUTH_PROVIDER_LAST_UNMOUNT__
        : null,
    });
    let disposed = false;
    
    // 🆕 Workers Auth: замість Supabase onAuthStateChange
    const authListener = workersAuth.onAuthStateChange(
      (event, nextSession) => {
        if (disposed) return;
        console.log("[AuthContext] 🔄 Workers Auth state changed:", {
          event,
          hasSession: !!nextSession,
          userId: nextSession?.user?.id,
          currentAuthStatus: authStatus,
          isCurrentlyAuthenticating: isAuthenticating,
          timestamp: new Date().toISOString(),
        });

        // Handle specific auth events for better cross-tab synchronization
        if (event === "SIGNED_OUT") {
          console.log(
            "[AuthContext] 🚪 SIGNED_OUT event detected - clearing all auth state",
          );
          setSession(null);
          setUser(null);
          setIsLoading(false);
          setIsAuthReady(true);
          setIsAuthenticating(false);
          trackAuthTransition("unauthenticated");
          return;
        }

        if (event === "TOKEN_REFRESHED") {
          console.log("[AuthContext] 🔄 TOKEN_REFRESHED event detected");
        }

        // 🆕 Конвертуємо WorkersAuthSession у Session для сумісності
        // ✅ CRITICAL FIX: Додано null-checks для nextSession.user
        const session = nextSession && nextSession.user ? {
          access_token: nextSession.access_token,
          refresh_token: nextSession.refresh_token,
          user: {
            ...nextSession.user,
            id: (nextSession.user.telegram_id ?? nextSession.user.id).toString(),
            email: `${nextSession.user.telegram_id ?? nextSession.user.id}@telegram.user`,
          },
        } as Session : null;

        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);
        setIsAuthReady(true);
        if (nextSession?.user) {
          console.log(
            "[AuthContext] ✅ Session detected, transitioning to loading_profile",
          );
          setIsAuthenticating(false); // Can safely clear authenticating state
          // CRITICAL FIX: Transition to loading_profile instead of authenticated
          // This prevents components from accessing null userProfile
          trackAuthTransition("loading_profile");
        } else {
          // Only move to unauthenticated if not currently authenticating
          setAuthStatus((prev) => {
            if (prev === "authenticating") {
              console.log(
                "[AuthContext] ⏸️ FSM staying in authenticating state (stable during auth flow)",
                {
                  isAuthenticating: prev === "authenticating",
                },
              );
              return prev; // keep FSM stable while authenticating or awaiting mock
            }
            console.log(
              "[AuthContext] ❌ No session, transitioning to unauthenticated",
            );
            setIsAuthenticating(false); // Clear authenticating state when transitioning away
            // use unified transition helper
            trackAuthTransition("unauthenticated");
            return "unauthenticated";
          });
        }
      },
    );

    console.log("[AuthContext] Getting initial Workers session...");
    // 🆕 Workers Auth: замість offerApi.supabase.auth.getSession()
    const storedSession = workersAuth.getStoredSession();
    if (!disposed) {
      // ✅ CRITICAL FIX: Додано null-checks для storedSession.user
      const session = storedSession && storedSession.user ? {
        access_token: storedSession.access_token,
        refresh_token: storedSession.refresh_token,
        user: {
          ...storedSession.user,
          id: (storedSession.user.telegram_id ?? storedSession.user.id).toString(),
          email: `${storedSession.user.telegram_id ?? storedSession.user.id}@telegram.user`,
        },
      } as Session : null;

      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
      setIsAuthReady(true);
      // CRITICAL FIX: Start with loading_profile if session exists
      trackAuthTransition(session?.user ? "loading_profile" : "unauthenticated");
    }

    return () => {
      disposed = true;
      window.__AUTH_PROVIDER_LAST_UNMOUNT__ = Date.now();
      console.log("[AuthContext] Cleaning up Workers auth listener (unmount)", {
        mountCount,
      });
      authListener?.subscription.unsubscribe();
    };
    // Note: authStatus and isAuthenticating intentionally excluded to prevent loop
    // CRITICAL FIX: trackAuthTransition removed from deps to prevent infinite re-render loop
    // trackAuthTransition is created with useCallback([trackEvent]) - stable reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependencies - listener should mount only once

  const {
    data: userProfile,
    isLoading: isProfileLoading,
    error: profileError,
    failureCount,
  } = useQuery({
    queryKey: ["userProfile", user?.id],
    queryFn: async () => {
      if (!user) return null;
      if (!profileStartRef.current) profileStartRef.current = performance.now();
      console.log("[AuthContext] Loading user profile for user ID:", user.id);
      try {
        const profile = await userApi.getUserProfile(user.id);
        if (!profile) {
          // 404 – новый пользователь или ещё не создан профиль
          console.log(
            "[AuthContext] User profile not found (null). Treating as new user candidate.",
          );
        }
        return profile;
      } catch (error) {
        console.error("[AuthContext] Failed to load user profile:", error);
        throw error;
      }
    },
    enabled: !!user,
    retry: (count, err) => {
      // Для нового пользователя позволим до 3 мягких повторов, иначе 1
      if (isNewUser) return count < 3;
      return count < 1;
    },
    staleTime: 5_000,
    // OPTIMIZATION: Disabled refetchOnWindowFocus for profile - it doesn't change that often
    // Profile updates are handled explicitly through mutations, not automatic refetches
    refetchOnWindowFocus: false,
  });

  // Таймаут: если через X секунд профиль не загрузился / ошибка - прекращаем общий лоадер
  useEffect(() => {
    if (!user || !isAuthReady) return;
    // Allow test override via global (non-production) for deterministic testing
    const timeoutOverride: number | undefined =
      typeof window !== "undefined"
        ? window.__AUTH_PROFILE_TIMEOUT_MS__
        : undefined;
    // OPTIMIZATION: Reduced from 6s to 3s - profile is created immediately during auth
    const timeoutMs = timeoutOverride ?? 1500; // жёсткий таймаут (было 6000ms)
    const timer = setTimeout(() => {
      if (isProfileLoading) {
        console.warn(
          "[AuthContext] Profile load hard timeout reached (1.5s). Proceeding without profile.",
        );
        setProfileLoadExpired(true);
      }
    }, timeoutMs);
    return () => clearTimeout(timer);
  }, [user, isAuthReady, isProfileLoading]);

  // Если профиль не найден (404), можно продолжать без него (например, новый пользователь) — останавливаем лоадер
  useEffect(() => {
    if (profileError) {
      console.warn(
        "[AuthContext] profileError detected -> ignoring as blocker",
        profileError,
      );
      setProfileLoadExpired(true);
      
      // CRITICAL FIX: If multiple retries failed (auth error like 401), transition to error state
      // This prevents infinite loading_profile loop when authentication actually failed
      if (failureCount >= 3 && authStatus === "loading_profile") {
        console.error(
          "[AuthContext] Profile load failed after multiple retries, transitioning to error state",
          { failureCount, profileError }
        );
        setAuthError("Failed to load user profile. Please try signing in again.");
        trackAuthTransition("error");
      }
    }
  }, [profileError, failureCount, authStatus, trackAuthTransition]);

  // CRITICAL FIX: Transition from loading_profile to ready when profile is loaded
  useEffect(() => {
    // Only transition if we're in loading_profile state
    if (authStatus !== "loading_profile") return;

    // Profile loaded successfully OR timeout/error occurred
    const profileReady = 
      (userProfile !== null && userProfile !== undefined) || 
      profileLoadExpired || 
      profileError;

    if (profileReady) {
      console.log(
        "[AuthContext] 🎉 Profile ready, transitioning to 'ready' state",
        {
          hasProfile: !!userProfile,
          profileExpired: profileLoadExpired,
          hasError: !!profileError,
        }
      );
      trackAuthTransition("ready");
    }
  }, [authStatus, userProfile, profileLoadExpired, profileError]); // Removed trackAuthTransition to fix infinite loop

  // 🆕 Workers Auth: Realtime removed - no longer needed
  // Profile updates will trigger manual cache invalidation

  // Identify user with PostHog when userProfile is available
  useEffect(() => {
    if (userProfile && user) {
      // ✅ CRITICAL FIX: Виправлено перевірку статусу (lowercase, без typos)
      if (userProfile.status === "banned" || userProfile.status === "suspended") {
        console.warn(
          "[AuthContext] User is",
          userProfile.status,
          "- logging out",
        );
        setAuthError(
          userProfile.status === "banned"
            ? "Your account has been banned"
            : "Your account is suspended",
        );
        // Sign out the user
        setTimeout(() => {
          workersAuth.signOut();
          workersAuth.notifySignOut();
        }, 100);
        return;
      }

      const newWindowMs = 5 * 60 * 1000;
      const recent =
        userProfile.created_at &&
        Date.now() - new Date(userProfile.created_at).getTime() < newWindowMs;
      if (recent && isNewUser === null) setIsNewUser(true);
      identifyUser(user.id, {
        role: userProfile.role,
        profileNickname: userProfile.profile_nickname || "",
        telegramId: userProfile.telegram_id,
        isVerified: userProfile.is_verified,
        // ✅ CRITICAL FIX: Видалено неіснуюче поле subscription_valid_until
        telegramUsername: userProfile.telegram_username || "",
      });
      if (recent) {
        trackEvent("user_signed_up", {
          role: userProfile.role,
          telegram_id: userProfile.telegram_id,
          is_premium: false,
        });
      }
    }
  }, [userProfile, user, identifyUser, trackEvent, isNewUser]);

  /**
   * Authenticate user with Telegram using validate-telegram-auth Edge Function
   * Includes Turnstile token for new users for bot protection
   * Includes browser fingerprint for multi-account detection
   */
  const authenticateWithTelegram = useCallback(
    async (initData: string, turnstileToken?: string): Promise<boolean> => {
      if (isAuthenticating) return false;
      if (isAuthenticationAttempted) return false;
      setIsAuthenticating(true);
      setIsAuthenticationAttempted(true);
      setAuthError(null);
      trackAuthTransition("authenticating");
      try {
        // Get browser fingerprint
        let visitorId: string | undefined;
        try {
          if (!window.__FINGERPRINT_AGENT__) {
            console.log("[AuthContext] Loading FingerprintJS agent...");
            window.__FINGERPRINT_AGENT__ = await FingerprintJS.load();
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const result = await (window.__FINGERPRINT_AGENT__ as any).get();
          visitorId = result.visitorId;
          console.log(
            "[AuthContext] Browser fingerprint obtained:",
            visitorId.substring(0, 8) + "...",
          );
        } catch (fpError) {
          console.warn(
            "[AuthContext] Failed to get fingerprint, continuing without it:",
            fpError,
          );
          // Continue without fingerprint in case FingerprintJS fails
        }

        // Cache URLSearchParams to avoid reparsing same initData multiple times
        const urlParams = new URLSearchParams(initData);
        const userParam = urlParams.get("user");
        const startParam = urlParams.get("start_param");

        if (userParam) {
          try {
            const telegramUser = JSON.parse(userParam);
            console.log("[AuthContext] Telegram user data:", {
              id: telegramUser.id,
              username: telegramUser.username,
            });
          } catch {
            console.warn(
              "[AuthContext] Failed to parse user data from initData",
            );
          }
        }

        // Log referral information if present
        if (startParam) {
          console.log("[AuthContext] Start parameter detected:", startParam);
          if (startParam.startsWith("ref_")) {
            const referrerId = startParam.substring(4);
            console.log(
              "[AuthContext] Referral detected - inviter ID:",
              referrerId,
            );
          }
        }

        securityMonitor.trackSecurityEvent(
          "telegram_auth_attempt",
          {
            has_turnstile_token: !!turnstileToken,
            has_start_param: !!startParam,
            is_referral: startParam?.startsWith("ref_") || false,
            has_fingerprint: !!visitorId,
          },
          "low",
        );
        if (import.meta.env.DEV) {
          console.log(
            "[AuthContext][DEV] AUTH_ATTEMPT_EDGE_CALL validate-telegram-auth",
          );
        }
        // REMOVED: Mock session injection (legacy Supabase code)
        // We only test with real Telegram authentication via Cloudflare Workers

        // Получаем сохраненную роль из sessionStorage
        const selectedRole = sessionStorage.getItem(ONBOARDING_ROLE_KEY);
        console.log(
          "[AuthContext] Selected role from sessionStorage:",
          selectedRole,
        );

        console.log("[AuthContext] 🔐 Starting Telegram authentication...", {
          hasTurnstile: !!turnstileToken,
          hasVisitorId: !!visitorId,
          selectedRole,
          timestamp: new Date().toISOString(),
        });

        // 🆕 Workers Auth: замість callEdgeFunction
        const response = await workersAuth.authenticateWithTelegram(
          initData,
          turnstileToken,
        ).catch(async (err: unknown) => {
          const anyErr = err as
            | {
                status?: number;
                error?: { status?: number };
                message?: string;
                name?: string;
              }
            | undefined;

          if (import.meta.env.DEV) {
            console.error("[AuthContext][DEV] Workers auth error:", {
              message: anyErr?.message,
              name: anyErr?.name,
            });

            // REMOVED: Mock session fallback (legacy Supabase code)
            // We only test with real Telegram authentication via Cloudflare Workers
          }

          return Promise.reject(anyErr);
        });
        if (import.meta.env.DEV) {
          console.log(
            "[AuthContext][DEV] Workers auth raw response:",
            JSON.stringify(
              {
                hasTokens: !!response.access_token && !!response.refresh_token,
                keys: Object.keys(response || {}),
                is_new_user: response.is_new_user,
              },
              null,
              2,
            ),
          );
        }
        const isNewUserFlag = response.is_new_user ?? false;
        setIsNewUser(isNewUserFlag);

        console.log("[AuthContext] ✅ Auth response received", {
          isNewUser: isNewUserFlag,
          hasTokens: !!response.access_token && !!response.refresh_token,
          timestamp: new Date().toISOString(),
        });

        if (!response.access_token || !response.refresh_token) {
          console.error("[AuthContext] ❌ Missing tokens in response:", {
            hasAccessToken: !!response.access_token,
            hasRefreshToken: !!response.refresh_token,
            responseKeys: Object.keys(response || {}),
            responseType: typeof response,
            isNull: response === null,
            isUndefined: response === undefined,
          });
          throw new Error("Invalid authentication response - missing tokens");
        }
        
        if (import.meta.env.DEV) {
          console.log(
            "[AuthContext][DEV] Session already stored by Workers Auth helper",
          );
        }
        
        // 🆕 Повідомляємо всіх слухачів про успішну аутентифікацію
        workersAuth.notifySignIn(response);

        console.log("[AuthContext] 🎉 Session created successfully", {
          userId: response.user.telegram_id,
          isNewUser: isNewUserFlag,
          timestamp: new Date().toISOString(),
        });

        // ✅ CRITICAL FIX: Always clear onboarding role after successful authentication
        // For new users: role was already saved to database by Worker
        // For existing users: role update (if any) was already processed by Worker
        // Keeping role in sessionStorage causes confusion and incorrect behavior
        const hadSavedRole = sessionStorage.getItem(ONBOARDING_ROLE_KEY);
        if (hadSavedRole) {
          sessionStorage.removeItem(ONBOARDING_ROLE_KEY);
          console.log(
            "[AuthContext] ✅ Cleared onboarding role from sessionStorage after successful auth",
            { 
              wasRole: hadSavedRole, 
              isNewUser: response.is_new_user,
              serverRole: response.user_role 
            }
          );
        }

        securityMonitor.trackSecurityEvent(
          "telegram_auth_success",
          {
            user_id: response.user.telegram_id,
            is_new_user: response.is_new_user,
          },
          "low",
        );

        // 🆕 Workers Auth: логування тепер відбувається на стороні Worker
        console.log("[AuthContext] Auth logging handled by Worker");

        // Save Telegram integration if chat_id is available
        if (response.is_new_user) {
          try {
            const urlParams = new URLSearchParams(initData);
            const chatParam = urlParams.get("chat");
            if (chatParam) {
              const chat = JSON.parse(chatParam);
              if (chat && chat.id) {
                // 🆕 Workers Auth: збереження Telegram integration тепер через Workers API
                console.log(
                  "[AuthContext] Telegram integration will be saved via Workers API",
                  { chatId: chat.id }
                );
                // TODO: Implement Workers API endpoint for saving Telegram integration
              }
            }
          } catch (error) {
            console.warn(
              "[AuthContext] Failed to save Telegram integration:",
              error,
            );
          }
        }

        // listener onAuthStateChange установит статус authenticated
        return true;
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Authentication failed";
        if (import.meta.env.DEV) {
          console.error(
            "[AuthContext][DEV] Authentication failure details:",
            e,
          );
        }
        setAuthError(msg);
        trackAuthTransition("error");
        securityMonitor.trackSecurityEvent(
          "telegram_auth_failed",
          { error_message: msg },
          "high",
        );

        // 🆕 Workers Auth: логування помилок тепер через Workers API
        console.log("[AuthContext] Auth failure logging handled by Worker", {
          error: msg,
          hasTurnstile: !!turnstileToken,
        });

        return false;
      } finally {
        setIsAuthenticating(false);
      }
    },
    [
      isAuthenticating,
      isAuthenticationAttempted,
      securityMonitor,
      trackAuthTransition,
    ],
  );

  const signOut = () => {
    // Reset PostHog tracking on logout
    if (posthog) {
      posthog.reset();
    }
    // Clear diagnostic globals to avoid sticky dev state when re-logging in
    if (typeof window !== "undefined") {
      delete window.__AUTH_PROFILE_TIMEOUT_MS__;
    }

    // Log the sign out action for cross-tab awareness
    console.log(
      "[AuthContext] User signing out - clearing session across all tabs",
    );

    // 🆕 Workers Auth: замість offerApi.supabase.auth.signOut()
    workersAuth.signOut();
    workersAuth.notifySignOut();
  };

  // CRITICAL FIX: Block UI until profile is fully loaded for ALL users
  // Previous logic only blocked for new users, causing race conditions
  const appLoading =
    authStatus === "initializing" ||
    authStatus === "authenticating" ||
    authStatus === "loading_profile";

  const value: AuthContextType = {
    session,
    user,
    userProfile: userProfile ?? null,
    isLoading: appLoading, // alias
    appLoading,
    // CRITICAL FIX: Only authenticated when profile is fully ready
    // This prevents components from accessing null userProfile
    isAuthenticated: 
      (authStatus === "ready" || authStatus === "loading_profile") && 
      !!session && 
      !!user,
    isAuthReady,
    isAuthenticating,
    authError,
    isAuthenticationAttempted,
    authStatus,
    isNewUser,
    profileLoadExpired,
    signOut,
    authenticateWithTelegram,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  return <RealAuthProvider>{children}</RealAuthProvider>;
};

export const useAuth = () => {
  // CRITICAL FIX: Add defensive checks for React context corruption
  // This can happen during HMR, lazy loading, or React version conflicts
  let context;

  try {
    context = useContext(AuthContext);
  } catch (error) {
    console.error("[useAuth] Failed to access AuthContext:", error);

    // Log additional diagnostic information
    if (import.meta.env.DEV) {
      console.error("[useAuth][DEV] React context error details:", {
        error,
        hasReact: typeof useContext !== "undefined",
        hasAuthContext: typeof AuthContext !== "undefined",
        authContextValue: AuthContext,
      });
    }

    throw new Error(
      "useAuth failed to access React Context. " +
        "This indicates a critical React runtime issue: " +
        "1) Multiple React instances in bundle, " +
        "2) React version mismatch, " +
        "3) Corrupted module during HMR. " +
        "Try clearing Vite cache and rebuilding: rm -rf node_modules/.vite && yarn dev",
    );
  }

  if (context === undefined || context === null) {
    throw new Error(
      "useAuth must be used within an AuthProvider. " +
        "This error typically occurs when: " +
        "1) Component is rendered outside AuthProvider tree, " +
        "2) Lazy-loaded component race condition with Context initialization, " +
        "3) Hot module reload in development. " +
        "Check that your component is properly wrapped in <AuthProvider>.",
    );
  }

  return context;
};
